/**
 * ProjectInfoSheet — T-11 "Project info" side panel for the delivery team.
 *
 * WHY a side panel, not an Overview card: the user decision was "keep it out of the agenda; it
 * only opens in a side panel when clicked". The Overview stays a delivery-health dashboard; the
 * presales/business context that came over from Oscar/HubSpot is one click away instead.
 *
 * The component owns BOTH the trigger button and the sheet so Radix can return focus to the
 * trigger on close (Esc, overlay click, or the X).
 *
 * Deliberately NOT rendered: deal amount, AWS segment, or any other financial figure. The panel
 * only reads an explicit allow-list of fields from `Project`, so a financial field arriving on the
 * payload is never displayed. The SA brief is untyped upstream, so its object keys are also
 * filtered against a financial-key pattern before rendering.
 *
 * Security: all content is rendered as React text nodes — never `dangerouslySetInnerHTML` — and
 * the only href built from data is an https-only SharePoint URL and a validated mailto.
 */
import { ExternalLink, Info } from "lucide-react";

import type { PresalesSaBrief, PresalesSaBriefSection, Project } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const HTTPS_URL_RE = /^https:\/\/\S+$/i;
const EMAIL_RE = /^[^\s@<>()"',;:]+@[^\s@<>()"',;:]+\.[^\s@<>()"',;:]+$/;
/** Object keys in untyped Oscar content that may carry a financial figure — never shown. */
const FINANCIAL_KEY_RE =
  /amount|price|pricing|cost|revenue|budget|usd|segment|discount|margin|(?:^|[_\s-])(?:arr|mrr|tcv|acv)(?:$|[_\s-])/i;
/** Guard against pathological nesting in untyped content. */
const MAX_DEPTH = 3;

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Newline-separated text → list items. Strips a leading bullet / number marker per line. */
function toLines(value: unknown): string[] {
  const t = text(value);
  if (!t) return [];
  return t
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "").trim())
    .filter((line) => line.length > 0);
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  // YYYY-MM-DD is parsed as LOCAL midnight (matches ProjectInfoCard) so it never shifts a day.
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function humanizeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** True when an untyped value would render something visible. */
function hasContent(value: unknown, depth = 0): boolean {
  if (value == null || depth > MAX_DEPTH) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number" || typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.some((v) => hasContent(v, depth + 1));
  if (isRecord(value))
    return Object.entries(value).some(
      ([k, v]) => !FINANCIAL_KEY_RE.test(k) && hasContent(v, depth + 1),
    );
  return false;
}

interface Contact {
  name: string | null;
  title: string | null;
  email: string | null;
}

/** Pull the three known fields from an untyped contact; unknown keys are ignored. */
function toContact(raw: Record<string, unknown>): Contact | null {
  const joined = [
    text(raw.first_name) ?? text(raw.firstname),
    text(raw.last_name) ?? text(raw.lastname),
  ]
    .filter(Boolean)
    .join(" ");
  const name = text(raw.name) ?? (joined || null);
  const title = text(raw.title) ?? text(raw.job_title) ?? text(raw.jobtitle);
  const email = text(raw.email);
  if (!name && !title && !email) return null;
  return { name, title, email };
}

function briefSectionHasContent(section: PresalesSaBriefSection): boolean {
  return (
    !!text(section.heading) ||
    hasContent(section.content) ||
    hasContent(section.items)
  );
}

function briefHasContent(brief: PresalesSaBrief | null | undefined): boolean {
  if (!brief) return false;
  return (
    !!text(brief.title) ||
    (Array.isArray(brief.sections) &&
      brief.sections.some((s) => isRecord(s) && briefSectionHasContent(s)))
  );
}

// ─── Presentational pieces ───────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: string[] }): JSX.Element {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-text-primary">
      {items.map((item, i) => (
        <li key={i} className="whitespace-pre-wrap break-words">
          {item}
        </li>
      ))}
    </ul>
  );
}

function Paragraph({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="whitespace-pre-wrap break-words text-sm text-text-primary">
      {children}
    </p>
  );
}

function MailLink({ email }: { email: string }): JSX.Element {
  if (!EMAIL_RE.test(email)) return <span>{email}</span>;
  return (
    <a
      href={`mailto:${email}`}
      className="text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {email}
    </a>
  );
}

/**
 * Render untyped Oscar content: strings → paragraphs, scalar arrays → bullets, objects / arrays of
 * objects → compact "key: value" lines. Financial keys are skipped.
 */
function UntypedValue({
  value,
  depth = 0,
}: {
  value: unknown;
  depth?: number;
}): JSX.Element | null {
  if (!hasContent(value, depth)) return null;

  if (typeof value === "string") return <Paragraph>{value.trim()}</Paragraph>;
  if (typeof value === "number" || typeof value === "boolean")
    return <Paragraph>{String(value)}</Paragraph>;

  if (Array.isArray(value)) {
    const visible = value.filter((v) => hasContent(v, depth + 1));
    const allScalar = visible.every(
      (v) =>
        typeof v === "string" ||
        typeof v === "number" ||
        typeof v === "boolean",
    );
    if (allScalar) {
      return <BulletList items={visible.map((v) => String(v).trim())} />;
    }
    return (
      <div className="space-y-2">
        {visible.map((v, i) => (
          <UntypedValue key={i} value={v} depth={depth + 1} />
        ))}
      </div>
    );
  }

  if (isRecord(value)) {
    const entries = Object.entries(value).filter(
      ([k, v]) => !FINANCIAL_KEY_RE.test(k) && hasContent(v, depth + 1),
    );
    return (
      <dl className="space-y-1 rounded-md border border-outlines p-2 text-sm">
        {entries.map(([k, v]) => {
          const scalar =
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean";
          return scalar ? (
            <div key={k} className="flex flex-wrap gap-x-1">
              <dt className="font-medium text-text-secondary">
                {humanizeKey(k)}:
              </dt>
              <dd className="whitespace-pre-wrap break-words text-text-primary">
                {String(v).trim()}
              </dd>
            </div>
          ) : (
            <div key={k} className="space-y-1">
              <dt className="font-medium text-text-secondary">
                {humanizeKey(k)}:
              </dt>
              <dd>
                <UntypedValue value={v} depth={depth + 1} />
              </dd>
            </div>
          );
        })}
      </dl>
    );
  }

  return null;
}

function SaBrief({ brief }: { brief: PresalesSaBrief }): JSX.Element {
  const title = text(brief.title);
  const generated = formatDate(brief.generated_at);
  const sections = (Array.isArray(brief.sections) ? brief.sections : []).filter(
    (s): s is PresalesSaBriefSection =>
      isRecord(s) && briefSectionHasContent(s),
  );
  return (
    <div className="space-y-3">
      {(title || generated) && (
        <div>
          {title && (
            <p className="text-sm font-medium text-text-primary">{title}</p>
          )}
          {generated && (
            <p className="text-xs text-text-secondary">Generated {generated}</p>
          )}
        </div>
      )}
      {sections.map((s, i) => (
        <div key={s.id ?? i} className="space-y-1.5">
          {text(s.heading) && (
            <h4 className="text-sm font-medium text-text-primary">
              {text(s.heading)}
            </h4>
          )}
          <UntypedValue value={s.content} />
          <UntypedValue value={s.items} />
        </div>
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface ProjectInfoSheetProps {
  project: Project;
}

export function ProjectInfoSheet({
  project,
}: ProjectInfoSheetProps): JSX.Element {
  const summary = text(project.project_summary);
  const businessLines = toLines(project.business_requirements);
  const technicalLines = toLines(project.technical_requirements);
  const companyOverview = text(project.company_overview);

  const aeName = text(project.account_executive?.name);
  const aeEmail = text(project.account_executive?.email);
  const saName = text(project.presales_sa_name);
  const contacts = (
    Array.isArray(project.customer_contacts) ? project.customer_contacts : []
  )
    .filter(isRecord)
    .map(toContact)
    .filter((c): c is Contact => c !== null);
  const hasPeople = !!(aeName || aeEmail || saName || contacts.length > 0);

  const closeDate = formatDate(project.hubspot_close_date);
  const brief = briefHasContent(project.presales_sa_brief)
    ? (project.presales_sa_brief as PresalesSaBrief)
    : null;
  const sharepointUrl =
    project.sharepoint_folder_url &&
    HTTPS_URL_RE.test(project.sharepoint_folder_url)
      ? project.sharepoint_folder_url
      : null;

  const isEmpty =
    !summary &&
    businessLines.length === 0 &&
    technicalLines.length === 0 &&
    !companyOverview &&
    !hasPeople &&
    !closeDate &&
    !brief &&
    !sharepointUrl;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <Info className="h-4 w-4" aria-hidden="true" />
          Project info
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto"
        data-testid="project-info-sheet"
      >
        <SheetHeader>
          <SheetTitle>Project info</SheetTitle>
          <SheetDescription>
            Presales and business context handed over to the delivery team.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isEmpty && (
            <p className="text-sm text-text-secondary">
              No presales information was received for this project.
            </p>
          )}

          {summary && (
            <Section title="Project summary">
              <Paragraph>{summary}</Paragraph>
            </Section>
          )}

          {businessLines.length > 0 && (
            <Section title="Business problem">
              <BulletList items={businessLines} />
            </Section>
          )}

          {technicalLines.length > 0 && (
            <Section title="Technical requirements">
              <BulletList items={technicalLines} />
            </Section>
          )}

          {companyOverview && (
            <Section title="Company overview">
              <Paragraph>{companyOverview}</Paragraph>
            </Section>
          )}

          {hasPeople && (
            <Section title="People">
              <dl className="space-y-2 text-sm">
                {(aeName || aeEmail) && (
                  <div>
                    <dt className="text-xs text-text-secondary">
                      Account Executive
                    </dt>
                    <dd className="text-text-primary">
                      {aeName && aeEmail && EMAIL_RE.test(aeEmail) ? (
                        <a
                          href={`mailto:${aeEmail}`}
                          className="text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          {aeName}
                        </a>
                      ) : aeName ? (
                        aeName
                      ) : (
                        <MailLink email={aeEmail as string} />
                      )}
                    </dd>
                  </div>
                )}
                {saName && (
                  <div>
                    <dt className="text-xs text-text-secondary">Presales SA</dt>
                    <dd className="text-text-primary">{saName}</dd>
                  </div>
                )}
                {contacts.length > 0 && (
                  <div>
                    <dt className="text-xs text-text-secondary">
                      Customer contacts
                    </dt>
                    <dd>
                      <ul className="space-y-1.5">
                        {contacts.map((c, i) => (
                          <li key={i} className="text-text-primary">
                            {c.name && (
                              <span className="font-medium">{c.name}</span>
                            )}
                            {c.title && (
                              <span className="text-text-secondary">
                                {c.name ? `, ${c.title}` : c.title}
                              </span>
                            )}
                            {c.email && (
                              <span className="block">
                                <MailLink email={c.email} />
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
              </dl>
            </Section>
          )}

          {closeDate && (
            <Section title="Deal">
              <dl className="text-sm">
                <dt className="text-xs text-text-secondary">
                  HubSpot close date
                </dt>
                <dd className="text-text-primary">{closeDate}</dd>
              </dl>
            </Section>
          )}

          {brief && (
            <Section title="SA brief">
              <SaBrief brief={brief} />
            </Section>
          )}

          {sharepointUrl && (
            <Section title="Links">
              <a
                href={sharepointUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                SharePoint folder
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </Section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ProjectInfoSheet;
